// Recovery for lazy-loaded chunks that 404 after a deploy: the old build's
// hashed assets stop being served, so a dynamic import() in a stale tab
// rejects. Reloading fetches the new index.html (and therefore new hashed
// URLs); hash routing means the user lands back on the same route.

const RELOAD_KEY = "chunk-reload-at";
const RELOAD_WINDOW_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? "");
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Unable to preload CSS/i.test(msg) ||
    (error instanceof TypeError && /Load failed|Failed to fetch/i.test(msg))
  );
}

/**
 * Reloads the page unless a chunk-error reload already happened in the last
 * 30s (so a chunk that is still missing after a fresh index.html cannot cause
 * a reload loop). Returns true if a reload was initiated.
 */
export function reloadOnceForChunkError(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    // sessionStorage unavailable (private mode) — fall through and reload
  }
  if (Date.now() - last < RELOAD_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore
  }
  window.location.reload();
  return true;
}

export function installChunkErrorListener(): void {
  // Vite dispatches vite:preloadError in production builds when a dynamic
  // import or one of its preloaded deps fails to load.
  window.addEventListener("vite:preloadError", (event: Event) => {
    if (reloadOnceForChunkError()) {
      // Suppress Vite's re-throw only when a reload is in flight; otherwise
      // let the error propagate to lazy() so the ErrorBoundary renders.
      event.preventDefault();
    }
  });
}
